import type { PushSubscribeDto, PushVapidPublicKeyResponseDto } from '@schwrz/shared';

import { api } from '../api/client';

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

async function getVapidPublicKey(): Promise<string | null> {
  const res = await api<PushVapidPublicKeyResponseDto>('/api/push/vapid-public-key');
  return res.publicKey;
}

export async function subscribeToPush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!isPushSupported()) {
    return { ok: false, reason: 'Браузер не поддерживает push-уведомления' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'Разрешение на уведомления не выдано' };
  }

  const publicKey = await getVapidPublicKey();
  if (!publicKey) {
    return { ok: false, reason: 'Push не настроен на сервере (VAPID)' };
  }

  const registration = await registerPushServiceWorker();
  if (!registration) {
    return { ok: false, reason: 'Не удалось зарегистрировать service worker' };
  }

  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
    });
  }

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    return { ok: false, reason: 'Некорректная подписка браузера' };
  }

  const payload: PushSubscribeDto = {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
  await api<{ ok: true }>('/api/push/subscribe', { method: 'POST', body: payload });
  return { ok: true };
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await api<{ ok: true }>('/api/push/subscribe', {
    method: 'DELETE',
    body: { endpoint },
  });
}

export async function fetchPushStatus(): Promise<{ supported: boolean; subscribed: boolean }> {
  if (!isPushSupported()) return { supported: false, subscribed: false };
  return api<{ supported: boolean; subscribed: boolean }>('/api/push/status');
}
